import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trophy, Package, Sparkles, MessageSquare, MessageCircle, Activity, Disc3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Tiny portal-less popover. We render absolutely in the doc, anchored near the
   click position. The parent controls open/close + anchor coords. */
export default function UserProfilePreview({ username, anchor, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/users/${username}/preview`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setData(null); setLoading(false); } });
    return () => { alive = false; };
  }, [username]);

  // Close on outside click + ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [onClose]);

  // Anchor positioning. Falls back to centered modal on small screens.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const style = isMobile
    ? { position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)", maxWidth: "92vw" }
    : anchor
      ? { position: "fixed", left: Math.min(anchor.x, window.innerWidth - 360), top: Math.min(anchor.y + 12, window.innerHeight - 460) }
      : { position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)" };

  return (
    <AnimatePresence>
      <>
        {isMobile && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-[#020B1F]/70 backdrop-blur-sm z-[120]" onClick={onClose}/>}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={style}
          className="z-[130] w-[340px] sm:w-[360px] rounded-3xl border border-[#00AFFF]/30 bg-[#041A3A]/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,175,255,0.25)] overflow-hidden"
          data-testid="user-profile-preview"
        >
          <div className="relative">
            {data?.banner ? (
              <img src={data.banner} alt="" className="w-full h-20 object-cover"/>
            ) : (
              <div className="w-full h-20 bg-gradient-to-br from-[#00AFFF] via-[#0044FF] to-[#020B1F]"/>
            )}
            <button onClick={onClose} aria-label="Schließen" className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-black/40 text-white/85 hover:bg-black/60" data-testid="upp-close"><X size={14}/></button>
          </div>

          {loading ? (
            <div className="px-5 py-6 text-center text-white/55 text-sm italic">Laden…</div>
          ) : !data ? (
            <div className="px-5 py-6 text-center text-white/55 text-sm italic">Nicht gefunden</div>
          ) : (
            <div className="px-5 pb-5 -mt-10 relative">
              <div className="flex items-end gap-3 mb-3">
                <div className="w-16 h-16 rounded-2xl border-2 border-[#020B1F] shadow-lg bg-[#020B1F] overflow-hidden shrink-0">
                  {data.avatar
                    ? <img src={data.avatar} alt={data.username} className="w-full h-full object-cover"/>
                    : <div className="w-full h-full grid place-items-center text-[#00AFFF] font-display font-black text-2xl bg-[#020B1F]">{(data.username || "?")[0].toUpperCase()}</div>}
                </div>
                <div className="flex-1 min-w-0 pb-1">
                  <div className="flex items-center gap-1.5">
                    <Link to={`/u/${data.username}`} onClick={onClose} className="font-display font-black text-white text-lg hover:text-[#00AFFF] transition truncate" data-testid="upp-username">@{data.username}</Link>
                    <span className={`w-2 h-2 rounded-full ${data.online ? "bg-[#22C55E]" : "bg-white/25"}`} title={data.online ? "online" : "offline"}/>
                  </div>
                  {data.rank && <div className="text-[10px] uppercase tracking-widest text-[#00AFFF] font-bold">{data.rank}</div>}
                </div>
              </div>

              {data.bio && <p className="text-xs text-white/70 font-body line-clamp-2 mb-3">{data.bio}</p>}

              {/* Title badge + badges */}
              {(data.title_badge || (data.badges || []).length > 0) && (
                <div className="flex items-center gap-1.5 flex-wrap mb-3">
                  {data.title_badge && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold" style={{ background: `${data.title_badge.color || "#00AFFF"}33`, color: data.title_badge.color || "#00AFFF" }}>
                      <Sparkles size={9}/> {data.title_badge.label}
                    </span>
                  )}
                  {(data.badges || []).slice(0, 4).map((b, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold bg-white/10 text-white/70">{b}</span>
                  ))}
                </div>
              )}

              {/* Stat grid */}
              <div className="grid grid-cols-3 gap-1.5 mb-3">
                <Stat icon={Trophy} label="Punkte" value={data.points || 0} color="#FBBF24"/>
                <Stat icon={Sparkles} label="Quiz" value={data.quiz?.best_score || 0} color="#00AFFF"/>
                <Stat icon={Package} label="Owned" value={data.collection?.owned || 0} color="#22C55E"/>
              </div>

              {/* Recent activity */}
              {(data.recent_activity || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-[9px] uppercase tracking-widest text-white/45 font-bold mb-1.5 inline-flex items-center gap-1"><Activity size={9}/>Aktivität</div>
                  <ul className="space-y-1">
                    {data.recent_activity.slice(0, 3).map((a, i) => {
                      const Ic = a.kind === "chat" ? MessageCircle : a.kind === "forum" ? MessageSquare : Disc3;
                      return (
                        <li key={i} className="flex items-start gap-2 text-[11px] text-white/70 font-body">
                          <Ic size={11} className="text-[#00AFFF] shrink-0 mt-0.5"/>
                          <span className="line-clamp-1">{a.preview || (a.kind === "chat" ? `#${a.room}` : a.kind)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="flex items-center gap-1.5">
                <Link to={`/u/${data.username}`} onClick={onClose} data-testid="upp-view-profile" className="flex-1 text-center px-3 py-1.5 rounded-full bg-[#00AFFF] text-[#020B1F] text-[10px] uppercase tracking-widest font-bold hover:bg-[#4DC3FF] transition">Profil</Link>
                <Link to={`/messages?with=${data.username}`} onClick={onClose} data-testid="upp-send-dm" className="flex-1 text-center px-3 py-1.5 rounded-full border border-[#00AFFF]/40 text-[#00AFFF] text-[10px] uppercase tracking-widest font-bold hover:bg-[#00AFFF]/10 transition">Nachricht</Link>
              </div>
            </div>
          )}
        </motion.div>
      </>
    </AnimatePresence>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl px-2 py-1.5" style={{ background: `${color}15`, border: `1px solid ${color}33` }}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold" style={{ color }}>
        <Icon size={9}/> {label}
      </div>
      <div className="text-white font-display font-black tabular-nums text-sm">{typeof value === "number" ? value.toLocaleString() : value}</div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Global controller — components anywhere on the page can call
   `openUserPreview(username, evt)` to summon the popover.                */
let _open = null;
export function openUserPreview(username, eventOrCoords) {
  if (_open) _open(username, eventOrCoords);
}
export function UserPreviewMount() {
  const [state, setState] = useState({ username: null, anchor: null });
  useEffect(() => {
    _open = (username, ev) => {
      let anchor = null;
      if (ev?.clientX != null) anchor = { x: ev.clientX, y: ev.clientY };
      else if (ev?.currentTarget?.getBoundingClientRect) {
        const r = ev.currentTarget.getBoundingClientRect();
        anchor = { x: r.left, y: r.bottom };
      } else if (ev?.x != null) anchor = ev;
      setState({ username, anchor });
    };
    return () => { _open = null; };
  }, []);
  const close = useCallback(() => setState({ username: null, anchor: null }), []);
  if (!state.username) return null;
  return <UserProfilePreview username={state.username} anchor={state.anchor} onClose={close}/>;
}
